import { Button, BUTTON_TYPES, FONT_TYPES, Text, TextButton } from '@zillow/constellation';
import StringConstants from '../constants/StringConstants';
import styled from 'styled-components';

const StyledPreWrapText = styled(Text)`
  white-space: pre-wrap;
`;

export const stringHelper = {
  camelCaseToUnderScoreAllCaps(str: string) {
    return str
      .replace(/([A-Z])/g, '_$1')
      .toLowerCase()
      .toUpperCase();
  },
  capitalize(str: string) {
    return str.charAt(0).toUpperCase() + str.slice(1);
  },
  currentYear() {
    return new Date().getFullYear();
  },
  pluralize(num: number, singular: string, plural: string) {
    return `${num} ${num === 1 ? singular : plural}`;
  },
  pluralizeWord(num: number, singular: string, plural: string) {
    return `${num === 1 ? singular : plural}`;
  },

  /**
   * Get a regex for the dashboard routes
   *
   * @param excludeList - a list of paths to exclude
   *  ex: ['dashboard/listing']
   * @param pathList - a list of possible paths to include
   *
   * @returns {String} - a regex of possible sub routes for the dashboard
   *  ex: '(overview|find-tenants|manage-tenants)'
   */
  getAllowDashboardParamsRegexByPaths({
    excludeList = [],
    pathList = [],
  }: {
    excludeList: Array<string>;
    pathList: Array<string>;
  }) {
    const filteredPathList = pathList.filter((value) => {
      return excludeList.includes(value) === false;
    });

    const subPathList = filteredPathList.map((value) => {
      return stringHelper.getLastPieceFromPath(value) || '';
    });

    const formattedRegex = (subPathList.length > 0 && `(${subPathList.join('|')})`) || '';

    return formattedRegex;
  },

  excludeWordsFromRegex({ excludeList = [], regexString = '' }: { excludeList: Array<string>; regexString: string }) {
    const filteredResponse = regexString
      .replace('(', '')
      .replace(')', '')
      .split('|')
      .filter((word) => {
        return !excludeList.includes(word);
      });

    const formattedRegex = (filteredResponse[0] !== '' && `(${filteredResponse.join('|')})`) || '';

    return formattedRegex;
  },

  getPageTitle(titleContent: string, brand: string) {
    const formattedBrand =
      StringConstants.FORMATTED_BRAND_NAMES_RENTAL[brand as keyof typeof StringConstants.FORMATTED_BRAND_NAMES_RENTAL];
    return `${titleContent} | ${formattedBrand}`;
  },
  wrapText(text = '', maxCharacters: number) {
    return `${text.slice(0, maxCharacters)}${text.length >= maxCharacters ? '…' : ''}`;
  },
  underScoreToSpaces(str = '') {
    return str.replace(/_/g, ' ');
  },
  capitalizeWords(str: string) {
    return str.split(' ').map(this.capitalize).join(' ');
  },
  getUrlQueryParams() {
    const query = window.location.search.substring(1);

    if (!query) {
      return false;
    }

    const queryString: { [key: string]: string | Array<string> } = {};
    const vars = query.split('&');
    let i: number;
    let pair: Array<string>;

    for (i = 0; i < vars.length; i++) {
      pair = vars[i].split('=');
      if (typeof queryString[pair[0]] === 'undefined') {
        queryString[pair[0]] = decodeURIComponent(pair[1]);
      } else if (typeof queryString[pair[0]] === 'string') {
        // @ts-ignore
        queryString[pair[0]] = [queryString[decodeURIComponent(pair[0])], decodeURIComponent(pair[1])];
      } else {
        // @ts-ignore
        queryString[pair[0]].push(decodeURIComponent(pair[1]));
      }
    }
    return queryString;
  },
  getCookieByName(name: string) {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);

    if (parts.length === 2) {
      return parts.pop()?.split(';').shift();
    }
    return null;
  },
  getLastPieceFromPath(pathname: string) {
    if (!pathname) {
      return '';
    }

    return pathname.split(/[/]+/).pop();
  },
  stripDollarSign(value: string) {
    return value.replace(/\$/g, '');
  },
  sanitizeReactInput(string: string) {
    let parsed = '';

    // react cannot inject "{" gracefully in strings
    // e.g. <div>`${"{"}`</div> will break
    if (string) {
      parsed = string.replace(/\{|\}/g, '');
    }

    return parsed;
  },
  isFontType(styling: string) {
    if (styling in FONT_TYPES) {
      return true;
    }
    return false;
  },
  parseStylings(stylings: Array<string>, defaultFontType: string) {
    let fontWeight;
    let fontType;

    stylings.forEach((styling) => {
      if (stringHelper.isFontType(styling)) {
        fontType = styling;
      } else {
        fontWeight = styling;
      }
    });

    if (!fontType) {
      fontType = defaultFontType;
    }

    return [fontWeight, fontType];
  },
  validateEmail: (email: string) => {
    /**
     * https://stackoverflow.com/a/63800465
     */
    const re =
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return re.test(String(email).toLowerCase());
  },
  // @ts-ignore
  buildText({ text, stylings, key, defaultFontType, url = undefined, onClick = () => {}, testId = null }) {
    let fontType = stylings.filter((style: string) => FONT_TYPES.hasOwnProperty(style));
    const [fontWeight] = stringHelper.parseStylings(stylings, defaultFontType);
    if (!fontType) {
      fontType = defaultFontType;
    }
    const buttonType = stylings.filter((style: string) => BUTTON_TYPES.hasOwnProperty(style));
    const propsForButton = {
      key,
      onClick,
      'data-test': testId,
      disabled: stylings.length > 0 ? stylings.includes('disabled') : false,
      fontType,
      buttonType: undefined,
    };
    if (buttonType.length > 0) {
      propsForButton.buttonType = buttonType[0];
    }

    if ((stylings.includes('primary') || stylings.includes('secondary')) && !stylings.includes('textLink')) {
      return <Button {...propsForButton}>{text}</Button>;
    }

    return (
      <StyledPreWrapText fontType={fontType} as={fontWeight === 'emphasize' ? 'b' : ''} onClick={onClick}>
        {url || stylings.includes('textLink') ? (
          <TextButton {...propsForButton} as={url && 'a'} href={url} target="_blank">
            {text}
          </TextButton>
        ) : (
          text
        )}
      </StyledPreWrapText>
    );
  },
};

export default stringHelper;
